import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { unichain } from 'viem/chains'

import { SUSDS_POINTS_MULTIPLIER } from '../../../consts'
import { ChainConfigEntry, SavingsStatsItem } from '../../types'
import { sortSavingsAccounts } from '../../utils'
import { chainLogo, tokenLogos } from './assets'
import { createTokenDefinitionRepository } from '@/domain/token-repository/TokenRepository'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { skySavingsGeneralStatsQueryOptionsFactory } from '@/features/savings/data/general-stats-query/skySavings'
import { skySavingsBackedByOptions } from '@/features/savings/data/savings-charts/backed-by-query/skySavingsBackedByQuery'
import {
  susdcSavingsRateQueryOptions,
  susdsSavingsRateQueryOptions,
} from '@/features/savings/data/savings-charts/savings-rate-query/skySavings'
import { ssrAuthOracleConverterQueryOptions } from '@/features/savings/logic/savings-converters/ssrAuthOracleConverter'

const usdc = new TokenDefinition({
  symbol: TokenSymbol('USDC'),
  name: 'Circle USD',
  decimals: 6,
  address: CheckedAddress('0x078D782b760474a361dDA0AF3839290b0EF57AD6'),
  logo: tokenLogos.usdc,
})

const usds = new TokenDefinition({
  symbol: TokenSymbol('USDS'),
  name: 'USDS Stablecoin',
  decimals: 18,
  address: CheckedAddress('0x7E10036Acc4B56d4dFCa3b77810356CE52313F9C'),
  logo: tokenLogos.usds,
})

const susds = new TokenDefinition({
  symbol: TokenSymbol('sUSDS'),
  name: 'Savings USDS',
  decimals: 18,
  address: CheckedAddress('0xA06b10Db9F390990364A3984C04FaDf1c13691b5'),
  logo: tokenLogos.susds,
})

const susdc = new TokenDefinition({
  symbol: TokenSymbol('sUSDC'),
  name: 'Savings USDC',
  decimals: 18,
  address: CheckedAddress('0x14d9143BEcC348920b68D123687045db49a016C6'),
  logo: tokenLogos.susdc,
})

export const unichainConfig: ChainConfigEntry = {
  originChainId: unichain.id,
  meta: {
    name: 'Unichain',
    logo: chainLogo,
  },
  tokenDefinitionRepository: createTokenDefinitionRepository([usdc, usds, susds, susdc]),
  nativeAssetInfo: {
    nativeAssetName: 'Ether',
    wrappedNativeAssetSymbol: TokenSymbol('WETH'),
    wrappedNativeAssetAddress: CheckedAddress('0x4200000000000000000000000000000000000006'),
    nativeAssetSymbol: TokenSymbol('ETH'),
    minRemainingNativeAssetBalance: NormalizedNumber(0.0005),
    nativeAssetLogo: tokenLogos.eth,
  },
  tokensWithMalformedApprove: [],
  tokensRequiringResetApproval: [],
  contractsRequiringUsdtResetApproval: [],
  permitSupport: {},
  sparkLend: undefined,
  savings: {
    priceConfigs: {
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [susds.symbol]: { type: 'ssr-auth-oracle' },
      [susdc.symbol]: { type: 'vault', assetsDecimals: 6 },
    },
    accounts: sortSavingsAccounts([
      {
        savingsToken: susdc.symbol,
        underlyingToken: usdc.symbol,
        supportedAssets: [usdc.symbol],
        fetchConverterQuery: ssrAuthOracleConverterQueryOptions,
        savingsRateQueryOptions: susdcSavingsRateQueryOptions,
        stats: {
          queryOptions: skySavingsGeneralStatsQueryOptionsFactory(susdc.symbol),
          items: ['tvl', 'users'] satisfies SavingsStatsItem[],
        },
        backedByQueryOptions: skySavingsBackedByOptions,
        liquidityChartQueryOptions: undefined,
        sparkPointsMultiplier: undefined,
      },
      {
        savingsToken: susds.symbol,
        underlyingToken: usds.symbol,
        supportedAssets: [usds.symbol, usdc.symbol],
        fetchConverterQuery: ssrAuthOracleConverterQueryOptions,
        savingsRateQueryOptions: susdsSavingsRateQueryOptions,
        stats: {
          queryOptions: skySavingsGeneralStatsQueryOptionsFactory(susds.symbol),
          items: ['tvl', 'users'] satisfies SavingsStatsItem[],
        },
        backedByQueryOptions: skySavingsBackedByOptions,
        liquidityChartQueryOptions: undefined,
        sparkPointsMultiplier: SUSDS_POINTS_MULTIPLIER,
      },
    ]),
    psmStables: [usdc.symbol, usds.symbol],
  },
  legacyFarms: undefined,
  spk: undefined,
  points: undefined,
  sparkRewards: undefined,
  swap: {
    priceConfigs: {
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [susds.symbol]: { type: 'ssr-auth-oracle' },
    },
  },
}
